/**
 * Receipt Correction Modal
 * 
 * Allows users to manually correct OCR results
 */

import { useState, useEffect } from 'react';
import { View, Modal, ScrollView, TextInput, Pressable, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import type { Expense, ExpenseCategory } from '@/services/expenses';
import { humanizeAsyncError } from '@/utils/asyncErrorMessage';

type ReceiptCorrectionModalProps = {
  visible: boolean;
  expense: Expense | null;
  onClose: () => void;
  onSave: (corrections: {
    amount?: number;
    currency?: string;
    merchant?: string;
    description?: string;
    date?: Date;
    category?: ExpenseCategory;
  }) => Promise<void>;
};

export function ReceiptCorrectionModal({
  visible,
  expense,
  onClose,
  onSave,
}: ReceiptCorrectionModalProps) {
  const theme = useTheme();
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('');
  const [merchant, setMerchant] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [category, setCategory] = useState('');
  const [saving, setSaving] = useState(false);
  const [amountError, setAmountError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);

  useEffect(() => {
    if (expense) {
      setAmount(expense.amount?.toString() || '');
      setCurrency(expense.currency || 'USD');
      setMerchant(expense.merchant || '');
      setDescription(expense.description || '');
      setDate(expense.date ? new Date(expense.date).toISOString().split('T')[0] : '');
      setCategory(expense.category || '');
      setAmountError(null);
      setDateError(null);
    }
  }, [expense]);

  const handleSave = async () => {
    if (!expense) return;
    let hasValidationError = false;
    setAmountError(null);
    setDateError(null);

    if (!amount.trim()) {
      setAmountError('Enter an amount before saving.');
      hasValidationError = true;
    } else {
      const parsedAmount = parseFloat(amount);
      if (Number.isNaN(parsedAmount) || parsedAmount < 0) {
        setAmountError('Enter a valid amount greater than or equal to 0.');
        hasValidationError = true;
      }
    }

    if (date.trim()) {
      const parsedDate = new Date(date);
      if (Number.isNaN(parsedDate.getTime())) {
        setDateError('Enter a valid date in YYYY-MM-DD format.');
        hasValidationError = true;
      }
    }

    if (hasValidationError) {
      return;
    }

    setSaving(true);
    try {
      const corrections: any = {};

      if (amount) {
        const parsedAmount = parseFloat(amount);
        if (!isNaN(parsedAmount)) {
          corrections.amount = parsedAmount;
        }
      }

      if (currency) {
        corrections.currency = currency;
      }

      if (merchant) {
        corrections.merchant = merchant;
      }

      if (description) {
        corrections.description = description;
      }

      if (date) {
        const parsedDate = new Date(date);
        corrections.date = parsedDate;
      }

      if (category) {
        corrections.category = category;
      }

      await onSave(corrections);
      Alert.alert('Saved', 'Receipt corrections saved.');
      onClose();
    } catch (error) {
      console.error('[ReceiptCorrectionModal] Error saving corrections:', error);
      Alert.alert('Unable to save', humanizeAsyncError(error, 'We couldn’t save your corrections. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const categories = [
    'Food & Dining',
    'Transportation',
    'Accommodation',
    'Entertainment',
    'Shopping',
    'Activities',
    'Other',
  ];

  if (!expense) return null;

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <View style={{ flex: 1, backgroundColor: theme.colors.background }}>
        {/* Header */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: theme.spacing(2),
            borderBottomWidth: 1,
            borderBottomColor: theme.colors.borderMuted,
          }}
        >
          <ThemedText variant="h3">Correct Receipt Details</ThemedText>
          <Pressable onPress={onClose}>
            <Ionicons name="close" size={24} color={theme.colors.textSecondary} />
          </Pressable>
        </View>

        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ padding: theme.spacing(2), gap: theme.spacing(2) }}
        >
          {/* OCR Raw Text Preview */}
          {expense.ocrRawText && (
            <Card variant="outlined">
              <View style={{ gap: theme.spacing(1) }}>
                <SubHeading>OCR Raw Text</SubHeading>
                <Body color={theme.colors.textSecondary} style={{ fontSize: 12, fontFamily: 'monospace' }}>
                  {expense.ocrRawText.substring(0, 500)}
                  {expense.ocrRawText.length > 500 ? '...' : ''}
                </Body>
              </View>
            </Card>
          )}

          {/* Amount */}
          <Card variant="outlined">
            <View style={{ gap: theme.spacing(1) }}>
              <Body style={{ fontWeight: '600' }}>Amount *</Body>
              <View style={{ flexDirection: 'row', gap: theme.spacing(1), alignItems: 'center' }}>
                <TextInput
                  style={{
                    flex: 1,
                    borderWidth: 1,
                    borderColor: theme.colors.borderMuted,
                    borderRadius: theme.radii.md,
                    padding: theme.spacing(1.5),
                    backgroundColor: theme.colors.surface,
                    color: theme.colors.text,
                  }}
                  value={amount}
                  onChangeText={(value) => {
                    setAmount(value);
                    if (amountError) setAmountError(null);
                  }}
                  placeholder="0.00"
                  keyboardType="decimal-pad"
                />
                <TextInput
                  style={{
                    width: 80,
                    borderWidth: 1,
                    borderColor: theme.colors.borderMuted,
                    borderRadius: theme.radii.md,
                    padding: theme.spacing(1.5),
                    backgroundColor: theme.colors.surface,
                    color: theme.colors.text,
                    textAlign: 'center',
                  }}
                  value={currency}
                  onChangeText={setCurrency}
                  placeholder="USD"
                  maxLength={3}
                  autoCapitalize="characters"
                />
              </View>
              {amountError ? <Caption color={theme.colors.error}>{amountError}</Caption> : null}
            </View>
          </Card>

          {/* Merchant */}
          <Card variant="outlined">
            <View style={{ gap: theme.spacing(1) }}>
              <Body style={{ fontWeight: '600' }}>Merchant</Body>
              <TextInput
                style={{
                  borderWidth: 1,
                  borderColor: theme.colors.borderMuted,
                  borderRadius: theme.radii.md,
                  padding: theme.spacing(1.5),
                  backgroundColor: theme.colors.surface,
                  color: theme.colors.text,
                }}
                value={merchant}
                onChangeText={setMerchant}
                placeholder="Merchant name"
              />
            </View>
          </Card>

          {/* Description */}
          <Card variant="outlined">
            <View style={{ gap: theme.spacing(1) }}>
              <Body style={{ fontWeight: '600' }}>Description</Body>
              <TextInput
                style={{
                  borderWidth: 1,
                  borderColor: theme.colors.borderMuted,
                  borderRadius: theme.radii.md,
                  padding: theme.spacing(1.5),
                  backgroundColor: theme.colors.surface,
                  color: theme.colors.text,
                  minHeight: 80,
                  textAlignVertical: 'top',
                }}
                value={description}
                onChangeText={setDescription}
                placeholder="Description"
                multiline
              />
            </View>
          </Card>

          {/* Date */}
          <Card variant="outlined">
            <View style={{ gap: theme.spacing(1) }}>
              <Body style={{ fontWeight: '600' }}>Date</Body>
              <TextInput
                style={{
                  borderWidth: 1,
                  borderColor: theme.colors.borderMuted,
                  borderRadius: theme.radii.md,
                  padding: theme.spacing(1.5),
                  backgroundColor: theme.colors.surface,
                  color: theme.colors.text,
                }}
                value={date}
                onChangeText={(value) => {
                  setDate(value);
                  if (dateError) setDateError(null);
                }}
                placeholder="YYYY-MM-DD"
              />
              <Caption color={theme.colors.textSecondary}>Format: YYYY-MM-DD</Caption>
              {dateError ? <Caption color={theme.colors.error}>{dateError}</Caption> : null}
            </View>
          </Card>

          {/* Category */}
          <Card variant="outlined">
            <View style={{ gap: theme.spacing(1) }}>
              <Body style={{ fontWeight: '600' }}>Category</Body>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(1) }}>
                {categories.map((cat) => (
                  <Pressable
                    key={cat}
                    onPress={() => setCategory(category === cat ? '' : cat)}
                    style={{
                      paddingHorizontal: theme.spacing(2),
                      paddingVertical: theme.spacing(1),
                      borderRadius: theme.radii.pill,
                      backgroundColor: category === cat ? theme.colors.primary : theme.colors.borderMuted,
                    }}
                  >
                    <Body style={{ color: category === cat ? '#ffffff' : theme.colors.text }}>
                      {cat}
                    </Body>
                  </Pressable>
                ))}
              </View>
            </View>
          </Card>
        </ScrollView>

        {/* Footer */}
        <View
          style={{
            flexDirection: 'row',
            gap: theme.spacing(1.5),
            padding: theme.spacing(2),
            borderTopWidth: 1,
            borderTopColor: theme.colors.borderMuted,
          }}
        >
          <Button label="Cancel" variant="ghost" onPress={onClose} style={{ flex: 1 }} />
          <Button
            label="Save"
            variant="primary"
            onPress={handleSave}
            loading={saving}
            style={{ flex: 1 }}
          />
        </View>
      </View>
    </Modal>
  );
}

